import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { TimedActionDraft } from "../../components/scheduledActions/scheduledActionTypes";

/** List by bank or by organization (exactly one id is supplied). */
interface GetTimedActionsArg {
  bankId?: string;
  organizationId?: string;
  token: string;
}

interface GetTimedActionByIdArg {
  id: string;
  token: string;
}

interface CreateTimedActionArg {
  body: TimedActionDraft;
  token: string;
}

interface UpdateTimedActionArg {
  id: string;
  body: TimedActionDraft;
  token: string;
}

interface DeleteTimedActionArg {
  id: string;
  token: string;
}

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const timedActionsAPI = createApi({
  reducerPath: "timedActionsAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetTimedActions"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL TIMED ACTIONS */
    getTimedActions: builder.query({
      query: (arg: GetTimedActionsArg) => ({
        url: arg.bankId
          ? `/timed-actions/?bank_id=${arg.bankId}`
          : `/timed-actions/?organization_id=${arg.organizationId}`,
        headers: { authorization: arg.token },
      }),
      providesTags: ["tagGetTimedActions"],
    }),
    //* *********************************************************** */
    //* GET ONE TIMED ACTION */
    getTimedActionById: builder.query({
      query: (arg: GetTimedActionByIdArg) => ({
        url: `/timed-actions/${arg.id}`,
        headers: { authorization: arg.token },
      }),
    }),
    //* *********************************************************** */
    //* CREATE TIMED ACTION */
    postTimedAction: builder.mutation({
      query: (arg: CreateTimedActionArg) => ({
        url: "/timed-actions/",
        method: "POST",
        body: arg.body,
        headers: { authorization: arg.token },
      }),
      extraOptions: { fbSerializer: "CreateTimedAction" },
      invalidatesTags: ["tagGetTimedActions"],
    }),
    //* *********************************************************** */
    //* EDIT TIMED ACTION */
    putTimedAction: builder.mutation({
      query: (arg: UpdateTimedActionArg) => ({
        url: `/timed-actions/${arg.id}`,
        method: "PUT",
        body: arg.body,
        headers: { authorization: arg.token },
      }),
      extraOptions: { fbSerializer: "UpdateTimedAction" },
      invalidatesTags: ["tagGetTimedActions"],
    }),
    //* *********************************************************** */
    //* DELETE TIMED ACTION */
    deleteTimedAction: builder.mutation({
      query: (arg: DeleteTimedActionArg) => ({
        url: `/timed-actions/${arg.id}`,
        method: "DELETE",
        headers: { authorization: arg.token },
      }),
      invalidatesTags: ["tagGetTimedActions"],
    }),
  }),
});

export const {
  useLazyGetTimedActionsQuery,
  useLazyGetTimedActionByIdQuery,
  usePostTimedActionMutation,
  usePutTimedActionMutation,
  useDeleteTimedActionMutation,
} = timedActionsAPI;
